import { useState, useEffect, useRef, useCallback } from "react";
import EmojiPicker from "emoji-picker-react";
import { FaSmile, FaPaperclip, FaPaperPlane } from "react-icons/fa";
import { getToken } from "../../function/token.jsx";
import domain from "../../../reusecomponent/domain.jsx";
import ImageUploadModal, {
  DocumentUploadModal,
  ErrorToastContainer,
  RecordAudio,
  SendAudio,
  SendDocument,
  SendImage,
  SendTextMessage,
  SendVideo,
  VideoUploadModal,
} from "../../function/CustomFunction.jsx";
import Header from "./chats_coponent/Header.jsx";
import Incoming from "./chats_coponent/incoming.jsx";
import Outgoing from "./chats_coponent/outgoing.jsx";

// Attachment menu items — defined outside to avoid re-creation per render
const ATTACHMENT_ITEMS = [
  { emoji: "📷", label: "Photo" },
  { emoji: "🎥", label: "Video" },
  { emoji: "🎙", label: "Audio" },
  { emoji: "🎙", label: "Record Audio" },
  { emoji: "📄", label: "Document" },
  { emoji: "💰", label: "Payment" },
];

export default function Customerchat({ wadetails, customerschat, setCustomerschat, onBackClick, onStartCall }) {
  const [text, setText] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [showAttachment, setShowAttachment] = useState(false);
  const [uploaderFiles, setUploaderOpen] = useState(null);
  const [documentUploader, setDocumentUploader] = useState(null);
  const [videoUploader, setVideoUploader] = useState(null);
  const [chatLoading, setChatLoading] = useState(true);

  const footerRef = useRef(null);
  const textareaRef = useRef(null);

  // ── Close emoji/attachment on outside click ───────────────────────────────
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (footerRef.current && !footerRef.current.contains(e.target)) {
        setShowEmoji(false);
        setShowAttachment(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const handleSend = useCallback(() => {
    if (text.trim() === "") return;
    SendTextMessage(wadetails, text, customerschat, setCustomerschat);
    setText("");
    // Reset textarea height
    if (textareaRef.current) textareaRef.current.style.height = "30px";
  }, [text, wadetails, customerschat, setCustomerschat]);

  const fetchContactschats = useCallback(async (page = null) => {
    if (page == null) {
      setChatLoading(true);
    }
    try {
      const token = getToken();
      const response = await fetch(
        page == null ? `${domain()}/contact-chat/${btoa(wadetails.wa_id)}?page=1` : page,
        { method: "GET", headers: new Headers({ Authorization: `Bearer ${token}` }), redirect: "follow" }
      );
      const result = await response.json();
      if (result.status) {
        let decodedData = {};
        if (typeof result.data === 'string') {
          try {
            decodedData = JSON.parse(atob(result.data));
          } catch (e) {
            decodedData = {};
          }
        } else {
          decodedData = result.data || {};
        }

        setCustomerschat({
          data: decodedData.data || (Array.isArray(decodedData) ? decodedData : []),
          next_page_url: decodedData.next_page_url || null,
        });
      } else {
        ErrorToastContainer(result.message);
      }
    } catch (_) {
      ErrorToastContainer("Internal Server Error");
    } finally {
      setChatLoading(false);
    }
  }, [wadetails, setCustomerschat]);

  useEffect(() => {
    setChatLoading(true);
    fetchContactschats();
  }, [wadetails, fetchContactschats]);

  const handleAttachmentAction = useCallback((label) => {
    setShowAttachment(false);
    switch (label) {
      case "Photo":    return SendImage(wadetails, setCustomerschat, setUploaderOpen);
      case "Video":    return SendVideo(wadetails, setCustomerschat, setVideoUploader);
      case "Audio":    return SendAudio(wadetails, setCustomerschat);
      case "Record Audio": return RecordAudio(wadetails, setCustomerschat);
      case "Document": return SendDocument(wadetails, setCustomerschat, setDocumentUploader);
      default: break; // Payment — not yet implemented
    }
  }, [wadetails, setCustomerschat]);

  return (
    <>
      {uploaderFiles && (
        <ImageUploadModal files={uploaderFiles} wadetails={wadetails} setCustomerschat={setCustomerschat} onClose={() => setUploaderOpen(null)} />
      )}
      {documentUploader && (
        <DocumentUploadModal uploadData={documentUploader} setCustomerschat={setCustomerschat} onClose={() => setDocumentUploader(null)} />
      )}
      {videoUploader && (
        <VideoUploadModal uploadData={videoUploader} setCustomerschat={setCustomerschat} onClose={() => setVideoUploader(null)} />
      )}

      <div className="d-flex flex-column h-100">
        <Header
          wadetails={wadetails}
          onRefresh={() => fetchContactschats()}
          onBackClick={onBackClick}
          onStartCall={onStartCall}
        />

        {/* Message list */}
        <div
          className="flex-grow-1 px-2 px-md-3 py-2"
          style={{
            backgroundColor: "#E5DDD5",
            minHeight: 0,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column-reverse",
          }}
        >
          {chatLoading ? (
            <div className="d-flex align-items-center justify-content-center m-auto py-4 text-muted gap-2">
              <div className="spinner-border spinner-border-sm text-secondary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <span style={{ fontSize: "14px", fontWeight: "500" }}>Chats are loading...</span>
            </div>
          ) : customerschat != null && customerschat.data && customerschat.data.length > 0 ? (
            customerschat.data.map((chat, index) =>
              chat.type === "outgoing" ? (
                <Outgoing chat={chat} key={chat.message_id || chat.client_system_id || chat.id || `out_${index}`} />
              ) : (
                <Incoming chat={chat} key={chat.message_id || chat.client_system_id || chat.id || `inc_${index}`} />
              )
            )
          ) : (
            <p className="text-center text-muted m-auto py-4" style={{ fontSize: "14px" }}>No messages yet</p>
          )}
        </div>

        {/* Input footer — position:relative so emoji/attachment popups anchor correctly */}
        <div
          ref={footerRef}
          className="wa-preview-footer position-relative"
          style={{ background: "#f0f0f0", padding: "10px" }}
        >
          {/* Emoji Picker */}
          {showEmoji && (
            <div style={{ position: "absolute", bottom: "70px", left: "10px", zIndex: 10 }}>
              <EmojiPicker
                open={showEmoji}
                onEmojiClick={(emoji) => setText(prev => prev + emoji.emoji)}
              />
            </div>
          )}

          {/* Attachment Menu */}
          {showAttachment && (
            <div
              style={{
                position: "absolute",
                bottom: "70px",
                left: "55px",
                zIndex: 10,
                background: "white",
                padding: "8px",
                borderRadius: "10px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                minWidth: "150px",
              }}
            >
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {ATTACHMENT_ITEMS.map(({ emoji, label }) => (
                  <li
                    key={label}
                    className="px-3 py-2 attachment-menu-item"
                    style={{ cursor: "pointer", borderRadius: "6px", fontSize: "14px" }}
                    onClick={() => handleAttachmentAction(label)}
                    role="menuitem"
                    tabIndex={0}
                    onKeyDown={e => e.key === "Enter" && handleAttachmentAction(label)}
                  >
                    {emoji} {label}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Input bar */}
          <div
            className="wa-input-container d-flex align-items-center gap-2"
            style={{
              background: "white",
              borderRadius: "25px",
              padding: "8px 15px",
            }}
          >
            <FaSmile
              size={22}
              style={{ cursor: "pointer", color: "#667781", flexShrink: 0 }}
              onClick={() => { setShowAttachment(false); setShowEmoji(prev => !prev); }}
              aria-label="Emoji picker"
              role="button"
            />

            <FaPaperclip
              size={20}
              style={{ cursor: "pointer", color: "#667781", flexShrink: 0 }}
              onClick={() => { setShowEmoji(false); setShowAttachment(prev => !prev); }}
              aria-label="Attachments"
              role="button"
            />

            <textarea
              ref={textareaRef}
              className="wa-input flex-grow-1"
              placeholder="Type a message"
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                e.target.style.height = "30px";
                e.target.style.height = Math.min(e.target.scrollHeight, 100) + "px";
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              style={{
                outline: "none",
                border: "none",
                boxShadow: "none",
                resize: "none",
                overflow: "hidden",
                width: "100%",
                fontSize: "16px",
                lineHeight: "20px",
                padding: "6px 0",
                maxHeight: "100px",
                height: "30px",
              }}
              aria-label="Message input"
            />

            <FaPaperPlane
              size={20}
              style={{
                cursor: text.trim() ? "pointer" : "default",
                color: text.trim() ? "rgb(39, 92, 83)" : "#adb5bd",
                flexShrink: 0,
                transition: "color 0.2s ease",
              }}
              onClick={handleSend}
              aria-label="Send message"
              role="button"
            />
          </div>
        </div>
      </div>
    </>
  );
}
